import { Head, router } from '@inertiajs/react'
import { ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { McpConnectProps } from '@/types/mcp'
import { CandidateNav } from '@/components/Shared/CandidateNav'

interface CommandBlockProps {
  command: string
}

function CommandBlock({ command }: CommandBlockProps) {
  const [copied, setCopied] = useState(false)

  const copyCommand = async () => {
    await navigator.clipboard.writeText(command)
    setCopied(true)
  }

  return (
    <div className="mb-3 rounded-card border border-border bg-card p-[22px]">
      <div className="mb-3 flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
          Copy &amp; paste into your terminal
        </p>
        <button
          type="button"
          onClick={copyCommand}
          className="h-[38px] shrink-0 rounded-[9px] border border-primary bg-background px-[14px] font-mono text-[13px] font-semibold text-primary"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-all rounded-[9px] bg-muted p-3 font-mono text-[12px] leading-[1.5] text-foreground">
        <code>{command}</code>
      </pre>
    </div>
  )
}

export default function McpConnect({ status, newToken, mcpEndpoint }: McpConnectProps) {
  const [confirmingReissue, setConfirmingReissue] = useState(false)
  const [revealedToken, setRevealedToken] = useState(newToken)

  useEffect(() => {
    if (newToken) {
      setRevealedToken(newToken)
      router.replaceProp('newToken', null)
    }
  }, [newToken])

  const issueToken = () => {
    setConfirmingReissue(false)
    router.post('/connect/token')
  }

  // ADR-015: the active token's decrypted secret always renders a working command. It survives
  // reloads (unlike the once-only `newToken` flash), so it's preferred; `revealedToken` remains
  // as a fallback for the instant a token is freshly issued this request, before the next
  // ConnectionStateService read would carry it as `status.tokenSecret`.
  const liveToken = status.tokenSecret ?? revealedToken
  const hasLiveToken = Boolean(liveToken)
  // No live token means only a legacy, pre-ADR-015 token exists (or none at all) -- its plaintext
  // was never stored, so it is unrecoverable. Generating a new token is the only way forward.
  const isLegacyOnly = !hasLiveToken

  // A failing `remove` (nothing registered yet, first-time connect) doesn't stop the next
  // line from running — bare sequential statements don't short-circuit on a non-zero exit,
  // only && does.
  const setupCommand = [
    'claude mcp remove hyrin',
    `claude mcp add --transport http hyrin ${mcpEndpoint} \\`,
    `  --header "Authorization: Bearer ${liveToken ?? ''}" \\`,
    '  && claude "Call get_intake_status on the hyrin MCP server right away — don\'t ask what I mean first. Then help me finish my Hyrin profile based on what it reports missing."',
  ].join('\n')

  const continueCommand =
    'claude "Call get_intake_status on the hyrin MCP server right away — don\'t ask what I mean ' +
    'first. Then help me continue my Hyrin profile based on what it reports missing."'

  return (
    <>
      <Head title="Connect your agent — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CandidateNav />

        <main className="mx-auto max-w-[720px] px-6 py-12">
          {hasLiveToken && !status.connected && (
            <>
              <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                Set up your profile
              </p>
              <h1 className="mb-4 font-heading text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
                Connect your AI agent
              </h1>
              <p className="mb-8 max-w-[560px] font-sans text-[16px] leading-[1.5] text-muted-foreground">
                Paste this into your agent once — it connects to Hyrin and starts building your
                profile.
              </p>

              <CommandBlock command={setupCommand} />

              <p className="mb-8 font-sans text-[13px] text-muted-foreground">
                This token is yours — it stays available here, so this command keeps working even
                if you come back to it later.
              </p>
            </>
          )}

          {hasLiveToken && status.connected && (
            <>
              <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                Resume your profile
              </p>
              <h1 className="mb-4 font-heading text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
                Your agent is connected
              </h1>
              <p className="mb-8 max-w-[560px] font-sans text-[16px] leading-[1.5] text-muted-foreground">
                To continue, open a terminal and run:
              </p>

              <CommandBlock command={continueCommand} />

              <p className="mb-8 font-sans text-[13px] text-muted-foreground">
                Your agent re-reads what&apos;s missing from the Hyrin server each time — nothing
                to re-register, no new token needed.
              </p>

              <details className="group mb-8">
                <summary className="flex cursor-pointer list-none items-center gap-1.5 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground [&::-webkit-details-marker]:hidden">
                  <ChevronRight className="size-3.5 shrink-0 transition-transform group-open:rotate-90" />
                  Set up on another machine, or rotate your token
                </summary>
                <div className="mt-4">
                  <p className="mb-3 font-sans text-[13px] text-muted-foreground">
                    Run this on another machine to connect it with the same token.
                  </p>
                  <CommandBlock command={setupCommand} />
                  <p className="mb-3 font-sans text-[13px] text-muted-foreground">
                    Rotating generates a new token and immediately revokes this one — any agent
                    still using it disconnects.
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfirmingReissue(true)}
                    className="h-[38px] rounded-[9px] border border-foreground bg-background px-4 font-sans text-[13px] font-semibold text-foreground hover:bg-muted"
                  >
                    Rotate token
                  </button>
                </div>
              </details>
            </>
          )}

          {isLegacyOnly && (
            <>
              <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                Set up your profile
              </p>
              <h1 className="mb-4 font-heading text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
                Get a fresh setup command
              </h1>
              <p className="mb-8 max-w-[560px] font-sans text-[16px] leading-[1.5] text-muted-foreground">
                Your existing token was issued before Hyrin started storing tokens securely, so it
                can no longer be shown here. Generate a new token to get a working command.
              </p>

              <button
                type="button"
                onClick={issueToken}
                className="mb-8 inline-flex h-[46px] items-center rounded-cta bg-foreground px-[22px] font-sans text-[15px] font-semibold text-background transition-opacity hover:opacity-90"
              >
                Generate a new token
              </button>
            </>
          )}

          <div className="mb-8 rounded-card border border-border bg-card p-[22px]">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
              Connection
            </p>

            <div className="flex items-center justify-between py-1.5">
              <span className="font-mono text-[13px] text-muted-foreground">status</span>
              <span className="flex items-center gap-2 font-mono text-[13px] text-foreground">
                <span
                  className={`size-2 rounded-full ${status.connected ? 'bg-primary' : 'bg-muted-foreground'}`}
                />
                {status.connected ? 'connected' : 'not connected'}
              </span>
            </div>

            {status.expiresAt && (
              <div className="flex items-center justify-between border-t border-border py-1.5">
                <span className="font-mono text-[13px] text-muted-foreground">expires</span>
                <span className="font-mono text-[13px] text-foreground">
                  {new Date(status.expiresAt).toLocaleString()}
                </span>
              </div>
            )}

            {status.tokenPreview && (
              <div className="mt-4 rounded-[9px] bg-muted p-4">
                {confirmingReissue ? (
                  <>
                    <p className="mb-4 font-sans text-[15px] text-foreground">
                      Generating a new token disconnects your current agent. Continue?
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={issueToken}
                        className="inline-flex h-[46px] items-center rounded-cta bg-foreground px-[22px] font-sans text-[15px] font-semibold text-background transition-opacity hover:opacity-90"
                      >
                        Yes, regenerate
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingReissue(false)}
                        className="h-[38px] rounded-[9px] border border-foreground bg-background px-4 font-sans text-[13px] font-semibold text-foreground hover:bg-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mb-3 font-sans text-[13px] text-muted-foreground">
                      Confirm this matches your agent config. Reissue if you need the full value
                      again.
                    </p>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[13px] text-muted-foreground">token</span>
                        <span className="font-mono text-[13px] font-semibold text-foreground">
                          {status.tokenPreview}
                        </span>
                      </div>
                      {/* The legacy-recovery state already surfaces a same-weight "Generate a
                          new token" CTA above — a second, differently-worded re-issue button
                          here would compete with it. */}
                      {!isLegacyOnly && (
                        <button
                          type="button"
                          onClick={() => setConfirmingReissue(true)}
                          className="h-[38px] shrink-0 rounded-[9px] bg-primary px-4 font-sans text-[13px] font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                        >
                          Re-issue token
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  )
}
